import { Point } from '@models/connector';

export function calculateRectPoints(rectangle) {
    const initialX = Number(rectangle.getAttribute('x'));
    const initialY = Number(rectangle.getAttribute('y'));
    const height = Number(rectangle.getAttribute('height'));
    const width = Number(rectangle.getAttribute('width'));
    const transformString = rectangle.getAttribute('transform');

    const rx = Math.min(Number(rectangle.getAttribute('rx') ?? 0), width / 2, height / 2);

    let rotation: { angle: number; cx: number; cy: number } | null = null;
    if (transformString) {
        const rotateMatch = transformString.match(
            /rotate\(\s*([-\d.]+)(?:deg)?\s*[,\s]\s*([-\d.]+)\s*[,\s]\s*([-\d.]+)\s*\)/
        );
        if (rotateMatch) {
            rotation = {
                angle: parseFloat(rotateMatch[1]),
                cx: parseFloat(rotateMatch[2]),
                cy: parseFloat(rotateMatch[3]),
            };
        }
    }

    function applyRotation(point: Point): Point {
        if (!rotation) return point;
        const rad = (rotation.angle * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const dx = point.x - rotation.cx;
        const dy = point.y - rotation.cy;
        return {
            x: rotation.cx + dx * cos - dy * sin,
            y: rotation.cy + dx * sin + dy * cos,
        };
    }

    const PI = Math.PI;
    const halfArcLen = (PI / 4) * rx;
    const sW = width - 2 * rx;
    const sH = height - 2 * rx;

    const cTL = { x: initialX + rx, y: initialY + rx };
    const cTR = { x: initialX + width - rx, y: initialY + rx };
    const cBR = { x: initialX + width - rx, y: initialY + height - rx };
    const cBL = { x: initialX + rx, y: initialY + height - rx };

    const arc = (c: Point, a: number): Point => ({
        x: c.x + rx * Math.cos(a),
        y: c.y + rx * Math.sin(a),
    });

    function sidePoint(
        d: number,
        halfArc1: number,
        arc1Fn: (t: number) => Point,
        straight: number,
        straightFn: (t: number) => Point,
        halfArc2: number,
        arc2Fn: (t: number) => Point
    ): Point {
        if (d <= halfArc1) return arc1Fn(d / halfArc1);
        d -= halfArc1;
        if (d <= straight) return straightFn(straight > 0 ? d / straight : 0.5);
        d -= straight;
        return arc2Fn(d / halfArc2);
    }

    function pointsOnSide(
        halfArc1: number,
        arc1Fn: (t: number) => Point,
        straight: number,
        straightFn: (t: number) => Point,
        halfArc2: number,
        arc2Fn: (t: number) => Point
    ): Point[] {
        const total = halfArc1 + straight + halfArc2;
        return [1 / 4, 2 / 4, 3 / 4].map(t =>
            sidePoint(t * total, halfArc1, arc1Fn, straight, straightFn, halfArc2, arc2Fn)
        );
    }

    const topPts = pointsOnSide(
        halfArcLen,
        t => arc(cTL, (5 * PI) / 4 + (t * PI) / 4),
        sW,
        t => ({ x: initialX + rx + t * sW, y: initialY }),
        halfArcLen,
        t => arc(cTR, (3 * PI) / 2 + (t * PI) / 4)
    );

    const rightPts = pointsOnSide(
        halfArcLen,
        t => arc(cTR, (7 * PI) / 4 + (t * PI) / 4),
        sH,
        t => ({ x: initialX + width, y: initialY + rx + t * sH }),
        halfArcLen,
        t => arc(cBR, 0 + (t * PI) / 4)
    );

    const bottomPts = pointsOnSide(
        halfArcLen,
        t => arc(cBR, PI / 4 + (t * PI) / 4),
        sW,
        t => ({ x: initialX + width - rx - t * sW, y: initialY + height }),
        halfArcLen,
        t => arc(cBL, PI / 2 + (t * PI) / 4)
    );

    const leftPts = pointsOnSide(
        halfArcLen,
        t => arc(cBL, (3 * PI) / 4 + (t * PI) / 4),
        sH,
        t => ({ x: initialX, y: initialY + height - rx - t * sH }),
        halfArcLen,
        t => arc(cTL, PI + (t * PI) / 4)
    );

    const topLeft = applyRotation(arc(cTL, (5 * PI) / 4));
    const topRight = applyRotation(arc(cTR, (7 * PI) / 4));
    const bottomRight = applyRotation(arc(cBR, PI / 4));
    const bottomLeft = applyRotation(arc(cBL, (3 * PI) / 4));

    return [
        topLeft,
        topRight,
        bottomLeft,
        bottomRight,
        ...topPts.map(applyRotation),
        ...rightPts.map(applyRotation),
        ...bottomPts.map(applyRotation),
        ...leftPts.map(applyRotation),
    ];
}
